import { useEffect, useMemo } from "react";
import {
  applyDiscountRules,
  formatCad,
  hourlyEstimateHours,
  lineTotalCents,
  linkedServicesFor,
  suggestBundles,
  sumLines,
  type BundleSuggestion,
  type CategoryWithServices,
  type DiscountRule,
  type LinkedServiceSuggestion,
  type Service,
  type ServiceVariantGroup,
} from "@shared/modules/x-atelier";
import { Button } from "../../../core/components/ui/button";
import { Icon } from "../../../core/components/ui/icon";
import { Input } from "../../../core/components/ui/input";
import { formatDuration } from "../lib/dates";
import { centsToInput, parseAmountToCents } from "../lib/money";
import { ServicePicker } from "./service-picker";

/** Ligne en cours d'édition : les montants restent en texte tant qu'on tape. */
export interface DraftLine {
  key: string;
  serviceId: string | null;
  label: string;
  price: string;
  durationMin: number | null;
  quantity: number;
  /** Rabais en % ; `discountLabel` posé = vient d'une règle, null = saisi à la main. */
  discountPct: number;
  discountLabel: string | null;
}

let counter = 0;
const nextKey = () => `l${Date.now().toString(36)}-${++counter}`;

export function lineFromService(s: Service): DraftLine {
  // Tarif horaire : le prix est le taux d'une heure, la quantité le nombre
  // d'heures estimé d'après la durée ; le montant final suit le temps requis.
  if (s.pricingUnit === "hourly") {
    return {
      key: nextKey(),
      serviceId: s.id,
      label: `${s.name} (à l’heure)`,
      price: centsToInput(s.priceCents ?? 0),
      durationMin: 60,
      quantity: hourlyEstimateHours(s),
      discountPct: 0,
      discountLabel: null,
    };
  }
  return {
    key: nextKey(),
    serviceId: s.id,
    label: s.name,
    price: centsToInput(s.priceCents ?? 0),
    durationMin: s.durationMin,
    quantity: 1,
    discountPct: 0,
    discountLabel: null,
  };
}

/** Services liés aux lignes : obligatoires manquants, recommandés à proposer. */
export function linkedForLines(
  lines: DraftLine[],
  services: Service[],
  vehicleClass?: import("@shared/modules/x-atelier").VehicleClass | null,
): { required: LinkedServiceSuggestion[]; recommended: LinkedServiceSuggestion[] } {
  const byId = new Map(services.map((s) => [s.id, s]));
  const selected = lines.flatMap((l) => {
    const s = l.serviceId ? byId.get(l.serviceId) : undefined;
    return s ? [s] : [];
  });
  if (!selected.length) return { required: [], recommended: [] };
  return linkedServicesFor(selected, services, vehicleClass ?? null);
}

/** Ajoute les services obligatoires manquants (déclinaison du véhicule). */
export function withRequiredLinks(
  lines: DraftLine[],
  services: Service[],
  vehicleClass?: import("@shared/modules/x-atelier").VehicleClass | null,
): DraftLine[] {
  const { required } = linkedForLines(lines, services, vehicleClass);
  if (!required.length) return lines;
  return [...lines, ...required.map((r) => lineFromService(r.service))];
}

/** Bandeau « recommandé avec … » : une phrase, un bouton. */
export function RecommendHint({
  suggestion,
  onApply,
  disabled,
}: {
  suggestion: LinkedServiceSuggestion;
  onApply: () => void;
  disabled?: boolean;
}) {
  return (
    <div className="bundle-hint bundle-hint--recommend" role="status">
      <span className="bundle-hint__icon" aria-hidden="true">
        <Icon name="sparkles" size={20} />
      </span>
      <span className="bundle-hint__text">
        Recommandé avec {suggestion.from.name.split(" — ")[0]} :{" "}
        <strong>{suggestion.service.name}</strong>
        {suggestion.link.note ? <> — {suggestion.link.note}</> : null}
      </span>
      <Button type="button" variant="ghost" size="sm" disabled={disabled} onClick={onApply}>
        Ajouter
      </Button>
    </div>
  );
}

export function emptyLine(): DraftLine {
  return {
    key: nextKey(),
    serviceId: null,
    label: "",
    price: "",
    durationMin: 30,
    quantity: 1,
    discountPct: 0,
    discountLabel: null,
  };
}

export function draftFromLine(l: {
  serviceId: string | null;
  label: string;
  priceCents: number;
  durationMin: number;
  quantity: number;
  discountPct?: number;
  discountLabel?: string | null;
}): DraftLine {
  return {
    key: nextKey(),
    serviceId: l.serviceId,
    label: l.label,
    price: centsToInput(l.priceCents),
    durationMin: l.durationMin,
    quantity: l.quantity,
    discountPct: l.discountPct ?? 0,
    discountLabel: l.discountLabel ?? null,
  };
}

/** Lignes prêtes pour l'API ; null si un montant est illisible. */
export function linesToInput(lines: DraftLine[]) {
  const out = [];
  for (const l of lines) {
    const priceCents = parseAmountToCents(l.price);
    if (priceCents === null || !l.label.trim() || l.durationMin === null) return null;
    out.push({
      serviceId: l.serviceId,
      label: l.label.trim(),
      priceCents,
      durationMin: Math.max(0, Math.round(l.durationMin) || 0),
      quantity: Math.max(1, Math.round(l.quantity) || 1),
      discountPct: Math.min(100, Math.max(0, Math.round(l.discountPct) || 0)),
      discountLabel: l.discountLabel,
    });
  }
  return out;
}

/** Forme lisible par les moteurs de rabais et de forfait. */
function discountable(lines: DraftLine[]) {
  return lines.map((l) => ({
    serviceId: l.serviceId,
    priceCents: parseAmountToCents(l.price) ?? 0,
    durationMin: l.durationMin ?? 0,
    quantity: l.quantity,
    discountPct: l.discountPct,
    discountLabel: l.discountLabel,
  }));
}

export function draftTotals(lines: DraftLine[]) {
  return sumLines(discountable(lines));
}

/** Les règles de rabais, réappliquées ; renvoie les mêmes lignes si rien ne change. */
export function withDiscountRules(lines: DraftLine[], rules: DiscountRule[]): DraftLine[] {
  if (rules.length === 0) return lines;
  const applied = applyDiscountRules(
    lines.map((l) => ({ ...l, priceCents: parseAmountToCents(l.price) ?? 0 })),
    rules,
  );
  const changed = applied.some(
    (l, i) =>
      l.discountPct !== lines[i]!.discountPct || l.discountLabel !== lines[i]!.discountLabel,
  );
  if (!changed) return lines;
  return applied.map(({ priceCents: _p, ...rest }) => rest);
}

/** Forfaits proposables d'après les lignes et le catalogue. */
export function bundleSuggestions(lines: DraftLine[], services: Service[]): BundleSuggestion[] {
  return suggestBundles(discountable(lines), services);
}

/**
 * Remplace les lignes composantes par le forfait : une unité de chaque
 * composant part (une ligne à quantité 2 passe à 1), le forfait s'ajoute.
 */
export function applyBundle(lines: DraftLine[], s: BundleSuggestion): DraftLine[] {
  const out: DraftLine[] = [];
  lines.forEach((l, i) => {
    if (!s.lineIndexes.includes(i)) out.push(l);
    else if (l.quantity > 1) out.push({ ...l, quantity: l.quantity - 1 });
  });
  out.push(lineFromService(s.bundle));
  return out;
}

/** Bandeau « ces services existent en forfait » : une phrase, un bouton. */
export function BundleHint({
  suggestion,
  onApply,
  disabled,
}: {
  suggestion: BundleSuggestion;
  onApply: () => void;
  disabled?: boolean;
}) {
  const n = suggestion.bundle.bundleServiceIds.length;
  return (
    <div className="bundle-hint" role="status">
      <span className="bundle-hint__icon" aria-hidden="true">
        <Icon name="sparkles" size={20} />
      </span>
      <span className="bundle-hint__text">
        Ces {n} services existent en forfait : <strong>{suggestion.bundle.name}</strong> à{" "}
        <strong>{formatCad(suggestion.bundle.priceCents ?? 0)}</strong> au lieu de{" "}
        {formatCad(suggestion.separateCents)}
        <span className="bundle-hint__savings">
          {" "}
          — économie de {formatCad(suggestion.savingsCents)}
        </span>
      </span>
      {/* type="button" : dans le formulaire du rendez-vous, il l'aurait soumis. */}
      <Button type="button" variant="primary" size="sm" disabled={disabled} onClick={onApply}>
        Remplacer par le forfait
      </Button>
    </div>
  );
}

interface LinesEditorProps {
  vehicleClass?: import("@shared/modules/x-atelier").VehicleClass | null;
  lines: DraftLine[];
  onChange: (lines: DraftLine[]) => void;
  /** Catalogue pour cocher des services et proposer les forfaits ; sans lui, seules les lignes libres sont possibles. */
  catalogue?: CategoryWithServices[];
  /** Règles de rabais automatiques, appliquées à chaque changement (aperçu ; l'API refait le calcul). */
  rules?: DiscountRule[];
  /** Services les plus cédulés, proposés en tête du sélecteur. */
  frequent?: Service[];
  /** Services déjà au rendez-vous à l'ouverture : en tête du sélecteur, catégorie ouverte. */
  priorityIds?: string[];
}

/**
 * Services cochés depuis le catalogue → lignes avec prix, durée, quantité et
 * rabais modifiables, plus des lignes libres. Les règles de rabais
 * s'appliquent toutes seules et le forfait est proposé quand ses services
 * sont cédulés séparément. Partagé par la modal de rendez-vous et la fiche.
 */
export function LinesEditor({
  lines,
  onChange,
  catalogue,
  vehicleClass,
  rules = [],
  frequent = [],
  priorityIds,
}: LinesEditorProps) {
  const checked = new Set(lines.flatMap((l) => (l.serviceId ? [l.serviceId] : [])));
  const totals = draftTotals(lines);
  const services = useMemo(() => (catalogue ?? []).flatMap((c) => c.services), [catalogue]);
  const suggestion = bundleSuggestions(lines, services)[0];
  const linked = linkedForLines(lines, services, vehicleClass);
  const recommendation = linked.recommended[0];

  // Les règles s'appliquent après chaque changement (cocher, retirer, quantité…).
  useEffect(() => {
    const next = withDiscountRules(lines, rules);
    if (next !== lines) onChange(next);
  }, [lines, rules, onChange]);

  // Choisir une variante : elle remplace celle du même service (« 2 tapis »
  // après « 1 tapis » n'ajoute pas une deuxième ligne), ou se retire d'un
  // second clic.
  const pick = (s: Service, group: ServiceVariantGroup) => {
    if (checked.has(s.id)) {
      onChange(lines.filter((l) => l.serviceId !== s.id));
      return;
    }
    const siblings = new Set(group.variants.map((v) => v.id));
    // Un service lié obligatoire (lavage avant décontamination…) s'ajoute d'office.
    onChange(
      withRequiredLinks(
        [...lines.filter((l) => !l.serviceId || !siblings.has(l.serviceId)), lineFromService(s)],
        services,
        vehicleClass,
      ),
    );
  };

  const patch = (key: string, changes: Partial<DraftLine>) =>
    onChange(lines.map((l) => (l.key === key ? { ...l, ...changes } : l)));

  return (
    <div className="lines-editor">
      {catalogue && catalogue.length > 0 ? (
        <ServicePicker
          categories={catalogue}
          selected={checked}
          onPick={pick}
          vehicleClass={vehicleClass}
          frequent={frequent}
          onRemove={(serviceId) => onChange(lines.filter((l) => l.serviceId !== serviceId))}
          priorityIds={priorityIds}
        />
      ) : null}

      {suggestion ? (
        <BundleHint
          suggestion={suggestion}
          onApply={() => onChange(applyBundle(lines, suggestion))}
        />
      ) : null}
      {recommendation ? (
        <RecommendHint
          suggestion={recommendation}
          onApply={() => onChange([...lines, lineFromService(recommendation.service)])}
        />
      ) : null}

      {lines.length > 0 ? (
        <div className="lines-editor__lines" role="table" aria-label="Lignes du rendez-vous">
          <div className="lines-editor__head" role="row">
            <span role="columnheader">Libellé</span>
            <span role="columnheader">Prix ($)</span>
            <span role="columnheader">Durée (min)</span>
            <span role="columnheader">Qté</span>
            <span role="columnheader">Rabais %</span>
            <span role="columnheader" className="lines-editor__head-remove">
              Retirer
            </span>
          </div>
          {lines.map((l) => (
            <div key={l.key} className="lines-editor__line" role="row">
              <span className="lines-editor__cell lines-editor__cell--label">
                <Input
                  aria-label="Libellé"
                  value={l.label}
                  onChange={(e) => patch(l.key, { label: e.target.value })}
                  placeholder="ex : Antirouille complet"
                />
                {l.discountPct > 0 ? (
                  <span className="lines-editor__discount-note">
                    −{l.discountPct} %
                    {l.discountLabel ? ` · ${l.discountLabel}` : " · rabais manuel"} ={" "}
                    {formatCad(
                      lineTotalCents({
                        priceCents: parseAmountToCents(l.price) ?? 0,
                        quantity: l.quantity,
                        discountPct: l.discountPct,
                      }),
                    )}
                  </span>
                ) : null}
              </span>
              <Input
                aria-label="Prix"
                inputMode="decimal"
                value={l.price}
                onChange={(e) => patch(l.key, { price: e.target.value })}
                placeholder="0,00"
              />
              <Input
                aria-label="Durée en minutes"
                type="number"
                min={0}
                step={5}
                value={l.durationMin ?? ""}
                placeholder="À préciser"
                required
                onChange={(e) =>
                  patch(l.key, {
                    durationMin: e.target.value === "" ? null : Number(e.target.value),
                  })
                }
              />
              <Input
                aria-label="Quantité"
                type="number"
                min={1}
                value={l.quantity}
                onChange={(e) => patch(l.key, { quantity: Number(e.target.value) })}
              />
              <Input
                aria-label="Rabais en pourcentage"
                type="number"
                min={0}
                max={100}
                className={l.discountLabel ? "lines-editor__auto" : undefined}
                title={l.discountLabel ? `Rabais automatique : ${l.discountLabel}` : undefined}
                value={l.discountPct}
                // Saisi à la main : la ligne quitte le régime automatique.
                onChange={(e) =>
                  patch(l.key, { discountPct: Number(e.target.value), discountLabel: null })
                }
              />
              <Button
                type="button"
                variant="ghost-danger"
                size="sm"
                className="btn--icon"
                aria-label={`Retirer la ligne ${l.label || "sans libellé"}`}
                icon={<Icon name="trash" size={16} />}
                onClick={() => onChange(lines.filter((x) => x.key !== l.key))}
              />
            </div>
          ))}
        </div>
      ) : (
        <p className="lines-editor__empty">
          Aucune ligne : cochez un service ou ajoutez une ligne libre.
        </p>
      )}

      <div className="lines-editor__foot">
        <Button
          type="button"
          variant="ghost"
          size="sm"
          icon={<Icon name="plus" size={14} />}
          onClick={() => onChange([...lines, emptyLine()])}
        >
          Ligne libre
        </Button>
        <span className="lines-editor__totals">
          {formatDuration(totals.durationMin)} · <strong>{formatCad(totals.totalCents)}</strong>
        </span>
      </div>
    </div>
  );
}
